import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  FolderOpen, FolderPlus, FileText, FilePdf, FileImage, FileDoc,
  LinkSimple, Download, Trash, UploadSimple, MagnifyingGlass,
  ArrowLeft, X, Plus, CaretRight, Eye,
} from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function prettyBytes(n = 0) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function docIcon(doc) {
  if (doc.kind === "link") return <LinkSimple size={20} />;
  const ct = (doc.content_type || "").toLowerCase();
  if (ct.includes("pdf")) return <FilePdf size={20} />;
  if (ct.startsWith("image/")) return <FileImage size={20} />;
  if (ct.includes("word") || ct.includes("officedocument.wordprocessing")) return <FileDoc size={20} />;
  return <FileText size={20} />;
}

export default function DocumentVault() {
  const { user } = useAuth();
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null); // null = All
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  // Upload modal
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFolder, setUploadFolder] = useState("Strategy");
  const [uploadTags, setUploadTags] = useState("");
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Link modal
  const [showLink, setShowLink] = useState(false);
  const [linkForm, setLinkForm] = useState({ title: "", url: "", folder: "Strategy", tags: "", description: "" });

  // New folder modal
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  // Preview modal
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewText, setPreviewText] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadFolders = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/vault/folders");
      setFolders(data.folders || []);
    } catch {
      toast.error("Could not load folders");
    }
  }, []);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeFolder) params.folder = activeFolder;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/vault/docs", { params });
      setDocs(data.items || []);
    } catch {
      toast.error("Could not load documents");
    } finally {
      setLoading(false);
    }
  }, [activeFolder, q]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    loadDocs();
  }, [loadDocs]);

  const totalCount = useMemo(() => folders.reduce((n, f) => n + (f.count || 0), 0), [folders]);

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("folder", uploadFolder);
      fd.append("tags", uploadTags);
      fd.append("description", uploadDesc);
      await api.post("/admin/vault/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      setShowUpload(false);
      setUploadFile(null); setUploadTags(""); setUploadDesc("");
      await loadFolders();
      await loadDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const handleAddLink = async () => {
    if (!linkForm.title.trim() || !linkForm.url.trim()) {
      toast.error("Title and URL required"); return;
    }
    try {
      await api.post("/admin/vault/link", {
        title: linkForm.title.trim(),
        url: linkForm.url.trim(),
        folder: linkForm.folder,
        tags: linkForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
        description: linkForm.description.trim(),
      });
      toast.success("Link added");
      setShowLink(false);
      setLinkForm({ title: "", url: "", folder: "Strategy", tags: "", description: "" });
      await loadFolders();
      await loadDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add link");
    }
  };

  const handleDownload = async (doc) => {
    if (doc.kind === "link") {
      window.open(doc.url, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const res = await api.get(`/admin/vault/docs/${doc.doc_id}/download`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: doc.content_type || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || doc.title;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        a.remove();
        window.URL.revokeObjectURL(url);
      }, 100);
      toast.success(`Downloaded ${doc.filename}`);
    } catch (e) {
      console.error("Download failed:", e);
      toast.error(e?.response?.data?.detail || e?.message || "Download failed");
    }
  };

  const isPreviewable = (doc) => {
    if (doc.kind === "link") return false;
    const ct = (doc.content_type || "").toLowerCase();
    return ct.startsWith("text/") || ct.includes("markdown") || ct.startsWith("image/");
  };

  const handlePreview = async (doc) => {
    if (!isPreviewable(doc)) { toast.error("Preview not available for this file type — try Download."); return; }
    setPreviewDoc(doc);
    setPreviewLoading(true);
    setPreviewText("");
    try {
      const res = await api.get(`/admin/vault/docs/${doc.doc_id}/download`, {
        responseType: "blob",
      });
      const ct = (doc.content_type || "").toLowerCase();
      if (ct.startsWith("image/")) {
        const url = window.URL.createObjectURL(res.data);
        setPreviewText(url);
      } else {
        const text = await res.data.text();
        setPreviewText(text);
      }
    } catch (e) {
      console.error("Preview failed:", e);
      toast.error(e?.response?.data?.detail || e?.message || "Preview failed");
      setPreviewDoc(null);
    } finally { setPreviewLoading(false); }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/vault/docs/${doc.doc_id}`);
      toast.success("Deleted");
      await loadFolders();
      await loadDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await api.post("/admin/vault/folders", { name });
      toast.success(`Folder "${name}" created`);
      setShowNewFolder(false);
      setNewFolderName("");
      await loadFolders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create folder");
    }
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-[color:var(--paper)]">
        <Navbar />
        <main className="max-w-2xl mx-auto px-6 py-24 text-center">
          <h1 className="font-serif text-4xl mb-4">Admin only</h1>
          <p className="text-[color:var(--ink-muted)]">This area is reserved for admin accounts.</p>
          <Link to="/dashboard" className="btn-ghost mt-8 inline-flex" data-testid="vault-back-dashboard">
            <ArrowLeft size={14}/> Back to dashboard
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--paper)]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10 fade-in">
        <Link to="/admin" className="text-sm link-underline inline-flex items-center gap-1 mb-6" data-testid="back-to-admin">
          <ArrowLeft size={14}/> Back to admin
        </Link>

        <div className="flex items-start justify-between flex-wrap gap-6 mb-8">
          <div>
            <div className="eyebrow mb-2">Admin · Document Vault</div>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Shared knowledge.</h1>
            <p className="text-[color:var(--ink-muted)] mt-2 max-w-xl">
              Research, strategy, metrics reviews, and institutional knowledge — all in one searchable place for the admin team.
            </p>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowLink(true)} className="btn-ghost" data-testid="btn-add-link">
              <LinkSimple size={14}/> Add link
            </button>
            <button onClick={() => setShowUpload(true)} className="btn-primary" data-testid="btn-upload">
              <UploadSimple size={14}/> Upload file
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-[240px_1fr] gap-8">
          {/* Sidebar */}
          <aside className="border rule bg-white">
            <div className="p-4 border-b rule flex items-center justify-between">
              <div className="eyebrow">Folders</div>
              <button onClick={() => setShowNewFolder(true)} className="text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]" aria-label="New folder" data-testid="btn-new-folder">
                <FolderPlus size={16}/>
              </button>
            </div>
            <button
              onClick={() => setActiveFolder(null)}
              className={`w-full text-left px-4 py-3 border-b rule text-sm flex items-center justify-between hover:bg-[color:var(--surface-alt)] ${activeFolder === null ? "bg-[color:var(--surface-alt)] font-medium" : ""}`}
              data-testid="folder-all"
            >
              <span className="inline-flex items-center gap-2"><FolderOpen size={14}/> All</span>
              <span className="font-mono text-xs text-[color:var(--ink-muted)]">{totalCount}</span>
            </button>
            {folders.map((f) => (
              <button
                key={f.name}
                onClick={() => setActiveFolder(f.name)}
                className={`w-full text-left px-4 py-3 border-b rule text-sm flex items-center justify-between hover:bg-[color:var(--surface-alt)] ${activeFolder === f.name ? "bg-[color:var(--surface-alt)] font-medium" : ""}`}
                data-testid={`folder-${f.name.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <span className="inline-flex items-center gap-2 truncate">
                  <CaretRight size={12} weight="bold" className="shrink-0"/>
                  <span className="truncate">{f.name}</span>
                </span>
                <span className="font-mono text-xs text-[color:var(--ink-muted)]">{f.count}</span>
              </button>
            ))}
          </aside>

          {/* Main list */}
          <section>
            <div className="mb-4 flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--ink-muted)]"/>
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search titles, tags, descriptions…"
                  className="w-full bg-white border rule pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[color:var(--ink)]"
                  data-testid="vault-search"
                />
              </div>
              {activeFolder && (
                <div className="text-xs font-mono text-[color:var(--ink-muted)]">
                  Filter: <span className="text-[color:var(--ink)]">{activeFolder}</span>
                  <button onClick={() => setActiveFolder(null)} className="ml-2 link-underline">clear</button>
                </div>
              )}
            </div>

            {loading ? (
              <div className="wave"><span/><span/><span/><span/><span/></div>
            ) : docs.length === 0 ? (
              <div className="border rule bg-white p-12 text-center" data-testid="vault-empty">
                <FolderOpen size={32} className="mx-auto text-[color:var(--ink-faint)]"/>
                <p className="mt-4 text-[color:var(--ink-muted)]">
                  {q ? "No documents match your search." : activeFolder ? `No documents in ${activeFolder} yet.` : "Vault is empty. Upload a file or add a link to start."}
                </p>
                <button onClick={() => setShowUpload(true)} className="btn-primary mt-6 inline-flex" data-testid="vault-empty-upload">
                  <UploadSimple size={14}/> Upload first document
                </button>
              </div>
            ) : (
              <div className="border rule bg-white divide-y rule" data-testid="vault-doc-list">
                {docs.map((doc) => (
                  <div key={doc.doc_id} className="p-4 flex items-start gap-4 hover:bg-[color:var(--surface-alt)] transition-colors" data-testid={`vault-doc-${doc.doc_id}`}>
                    <div className="shrink-0 pt-1 text-[color:var(--ink-muted)]">{docIcon(doc)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="font-serif text-lg leading-snug break-words">{doc.title}</div>
                          <div className="mt-1 text-xs text-[color:var(--ink-muted)] font-mono">
                            {doc.folder} · {prettyBytes(doc.size_bytes)} · {doc.uploaded_by_name || doc.uploaded_by_email} · {new Date(doc.created_at).toLocaleDateString()}
                          </div>
                          {doc.description && (
                            <p className="mt-2 text-sm text-[color:var(--ink-muted)]">{doc.description}</p>
                          )}
                          {doc.tags?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {doc.tags.map((t) => (
                                <span key={t} className="text-[10px] font-mono uppercase tracking-widest border rule px-2 py-0.5">{t}</span>
                              ))}
                            </div>
                          )}
                          {doc.kind === "link" && (
                            <div className="mt-2 text-xs text-[color:var(--oxford)] font-mono break-all">{doc.url}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {isPreviewable(doc) && (
                            <button
                              onClick={() => handlePreview(doc)}
                              className="btn-ghost py-2 px-3 text-xs"
                              data-testid={`vault-preview-${doc.doc_id}`}
                            >
                              <Eye size={14}/> Preview
                            </button>
                          )}
                          <button
                            onClick={() => handleDownload(doc)}
                            className="btn-ghost py-2 px-3 text-xs"
                            data-testid={`vault-open-${doc.doc_id}`}
                          >
                            {doc.kind === "link" ? <LinkSimple size={14}/> : <Download size={14}/>}
                            {doc.kind === "link" ? "Open" : "Download"}
                          </button>
                          {user?.is_super && (
                            <button
                              onClick={() => handleDelete(doc)}
                              className="text-[color:var(--ink-faint)] hover:text-[color:var(--vermilion)] p-2"
                              aria-label="Delete"
                              data-testid={`vault-delete-${doc.doc_id}`}
                            >
                              <Trash size={16}/>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>

      {/* Upload modal */}
      {showUpload && (
        <Modal title="Upload a file" onClose={() => setShowUpload(false)}>
          <div className="space-y-4">
            <input
              type="file"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="w-full text-sm"
              data-testid="upload-file-input"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.png,.jpg,.jpeg,.webp"
            />
            <FolderSelect folders={folders} value={uploadFolder} onChange={setUploadFolder} testid="upload-folder" />
            <label className="block">
              <div className="eyebrow mb-1">Tags (comma-separated)</div>
              <input
                value={uploadTags}
                onChange={(e) => setUploadTags(e.target.value)}
                placeholder="research, q2-2026, internal"
                className="w-full bg-transparent border rule px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--ink)]"
                data-testid="upload-tags"
              />
            </label>
            <label className="block">
              <div className="eyebrow mb-1">Description (optional)</div>
              <textarea
                value={uploadDesc}
                onChange={(e) => setUploadDesc(e.target.value)}
                rows={3}
                className="w-full bg-transparent border rule px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--ink)]"
                data-testid="upload-desc"
              />
            </label>
            <div className="text-xs text-[color:var(--ink-muted)] font-mono">
              Max 20 MB · PDF, Word, Excel, PowerPoint, images, text
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setShowUpload(false)} className="btn-ghost" data-testid="upload-cancel">Cancel</button>
              <button
                onClick={handleUpload}
                disabled={!uploadFile || uploading}
                className="btn-primary disabled:opacity-40"
                data-testid="upload-submit"
              >
                <UploadSimple size={14}/> {uploading ? "Uploading…" : "Upload"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Link modal */}
      {showLink && (
        <Modal title="Add an external link" onClose={() => setShowLink(false)}>
          <div className="space-y-4">
            <label className="block">
              <div className="eyebrow mb-1">Title</div>
              <input
                value={linkForm.title}
                onChange={(e) => setLinkForm({ ...linkForm, title: e.target.value })}
                placeholder="Q1 Marketing Plan"
                className="w-full bg-transparent border rule px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--ink)]"
                data-testid="link-title"
              />
            </label>
            <label className="block">
              <div className="eyebrow mb-1">URL</div>
              <input
                value={linkForm.url}
                onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })}
                placeholder="https://docs.google.com/document/…"
                className="w-full bg-transparent border rule px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--ink)]"
                data-testid="link-url"
              />
            </label>
            <FolderSelect folders={folders} value={linkForm.folder} onChange={(v) => setLinkForm({ ...linkForm, folder: v })} testid="link-folder" />
            <label className="block">
              <div className="eyebrow mb-1">Tags</div>
              <input
                value={linkForm.tags}
                onChange={(e) => setLinkForm({ ...linkForm, tags: e.target.value })}
                placeholder="drive, marketing"
                className="w-full bg-transparent border rule px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--ink)]"
                data-testid="link-tags"
              />
            </label>
            <label className="block">
              <div className="eyebrow mb-1">Description (optional)</div>
              <textarea
                value={linkForm.description}
                onChange={(e) => setLinkForm({ ...linkForm, description: e.target.value })}
                rows={3}
                className="w-full bg-transparent border rule px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--ink)]"
                data-testid="link-desc"
              />
            </label>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setShowLink(false)} className="btn-ghost" data-testid="link-cancel">Cancel</button>
              <button onClick={handleAddLink} className="btn-primary" data-testid="link-submit">
                <Plus size={14}/> Add link
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* New folder modal */}
      {showNewFolder && (
        <Modal title="Create folder" onClose={() => setShowNewFolder(false)}>
          <div className="space-y-4">
            <label className="block">
              <div className="eyebrow mb-1">Folder name</div>
              <input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="e.g. Compliance 2026"
                className="w-full bg-transparent border rule px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--ink)]"
                autoFocus
                data-testid="new-folder-name"
              />
            </label>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowNewFolder(false)} className="btn-ghost" data-testid="new-folder-cancel">Cancel</button>
              <button onClick={handleCreateFolder} className="btn-primary" data-testid="new-folder-submit">
                <FolderPlus size={14}/> Create
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Preview modal */}
      {previewDoc && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 fade-in" onClick={() => setPreviewDoc(null)}>
          <div className="bg-white border rule max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b rule">
              <div className="min-w-0 flex-1">
                <div className="eyebrow">Preview</div>
                <div className="font-serif text-lg truncate">{previewDoc.title}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => handleDownload(previewDoc)} className="btn-ghost text-xs py-2 px-3" data-testid="preview-download">
                  <Download size={14}/> Download
                </button>
                <button onClick={() => setPreviewDoc(null)} className="text-[color:var(--ink-muted)] hover:text-[color:var(--ink)] p-2" aria-label="Close" data-testid="preview-close">
                  <X size={18}/>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-6">
              {previewLoading ? (
                <div className="wave"><span/><span/><span/><span/><span/></div>
              ) : (previewDoc.content_type || "").startsWith("image/") ? (
                <img src={previewText} alt={previewDoc.title} className="max-w-full mx-auto"/>
              ) : (previewDoc.content_type || "").includes("markdown") || previewDoc.filename?.toLowerCase().endsWith(".md") ? (
                <article className="prose prose-slate max-w-none font-serif" data-testid="preview-markdown">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{previewText}</ReactMarkdown>
                </article>
              ) : (
                <pre className="whitespace-pre-wrap text-sm font-mono leading-relaxed" data-testid="preview-text">{previewText}</pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 fade-in" onClick={onClose}>
      <div className="bg-white border rule max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="eyebrow">{title}</div>
          <button onClick={onClose} className="text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]" aria-label="Close">
            <X size={18}/>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FolderSelect({ folders, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="eyebrow mb-1">Folder</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border rule px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--ink)]"
        data-testid={testid}
      >
        {folders.map((f) => (
          <option key={f.name} value={f.name}>{f.name}</option>
        ))}
      </select>
    </label>
  );
}
